import { destroyBrands, useBrands } from '@gozoki/api'
import { includesLowerCase } from '@gozoki/tools'
import { TextField } from '@mui/material'
import type { GridColDef } from '@mui/x-data-grid'
import { useMutation } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { AuthorizedButtonLink } from '~/utils/rbac'
import { ConfirmResourceDeletionButton } from '../../communication/ConfirmDeletionButton'
import SimpleDataGrid from '../../containers/SimpleDataGrid'
import Thumbnail from '../../icons/Thumbnail'

const marquesColDef: GridColDef[] = [
  {
    field: 'image',
    headerName: 'Image',
    renderCell: (url) => <Thumbnail url={url.value} alt="Marque" rounded />,
    width: 150,
  },
  { field: 'name', headerName: 'Marque', width: 300 },
]

export const BrandsPanel = () => {
  const [brandsSelection, setBrandsSelection] = useState<number[]>([])
  const [brandsSearch, setBrandsSearch] = useState('')

  const brands = useBrands()

  // Transform fetched brands into DataGrid format
  const brandsRows = useMemo(() => {
    if (brands.data === undefined) {
      return []
    }
    return brands.data
      .filter((brand) => includesLowerCase(brand.name, brandsSearch))
      .map((brand) => ({
        id: brand.id,
        image: brand.image?.url,
        name: brand.name,
      }))
  }, [brands.data, brandsSearch])

  const deleteBrandMut = useMutation({
    mutationFn: destroyBrands,
  })

  const handleDeleteBrand = () => {
    const deletionPromise = deleteBrandMut.mutateAsync(brandsSelection, {
      onSuccess: () => {
        brands.refetch()
      },
    })
    setBrandsSelection([]) // Avoid disabled button flickering
    return deletionPromise
  }

  const brandsDisabled = brandsSelection.length === 0 || deleteBrandMut.isPending

  return (
    <>
      <div style={{ alignItems: 'center', display: 'flex', padding: '16px' }}>
        <TextField
          variant="outlined"
          label="Recherche"
          value={brandsSearch}
          onChange={(e) => setBrandsSearch(e.target.value)}
        />
        <div style={{ flexGrow: 1 }} />
        <ConfirmResourceDeletionButton
          handleResourceDeletion={handleDeleteBrand}
          disabled={brandsDisabled}
        />
        <AuthorizedButtonLink
          variant="contained"
          sx={{ height: '36px' }}
          to="/categories/brand/new"
          search={{ brandId: brandsSelection[0] }}
          disabled={brandsDisabled}
        >
          Modifier
        </AuthorizedButtonLink>
      </div>
      <SimpleDataGrid
        rowHeight={100} // For image display
        columns={marquesColDef}
        rows={brandsRows}
        checkboxSelection
        loading={brands.isLoading || deleteBrandMut.isPending || brands.isRefetching}
        onRowSelectionModelChange={(newSelection) => setBrandsSelection(newSelection as number[])}
      />
    </>
  )
}
